import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sharepointService } from '../services/SharePointService';
import './Planning.css';

interface PlanningData {
  [person: string]: {
    [week: number]: {
      Id?: number;
      sourceWeek?: number;
      project: string;
      customer?: string;
      percentage: number;
      isLongTerm?: boolean;
    }[];
  };
}

const Planning: React.FC = () => {
  const { getAccessToken } = useAuth();
  const [planningData, setPlanningData] = useState<PlanningData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weeks, setWeeks] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'twoMonths' | 'fullYear'>('twoMonths');
  const [persons, setPersons] = useState<string[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [customerAnsvarig, setCustomerAnsvarig] = useState<{ [customerTitle: string]: string }>({});
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [personSuggestions, setPersonSuggestions] = useState<string[]>([]);
  const [tenantUsers, setTenantUsers] = useState<string[]>([]);
  const [tenantUsersLoaded, setTenantUsersLoaded] = useState(false);
  const [formData, setFormData] = useState({ person: '', selectedWeeks: [] as number[], customer: '', projects: [{ project: '', percentage: 100 }] as { project: string; percentage: number }[], isUnavailable: false });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; person: string; week: number; index: number; allocation: any } | null>(null);
  const [projectEditDialog, setProjectEditDialog] = useState<{ open: boolean; projectId: number; projectTitle: string; status: string; saljstatus: string; type: string; fte: number; autoFte: boolean; tidKvar: number; timmarVarjeVecka: number; loading: boolean }>({
    open: false, projectId: 0, projectTitle: '', status: '', saljstatus: '', type: '', fte: 0, autoFte: false, tidKvar: 0, timmarVarjeVecka: 0, loading: false
  });
  const [deleteProjectDialog, setDeleteProjectDialog] = useState<{ open: boolean; projectId: number; projectTitle: string; loading: boolean } | null>(null);
  const [statusChoices, setStatusChoices] = useState<string[]>([]);
  const [saljstatusChoices, setSaljstatusChoices] = useState<string[]>([]);
  const [typeChoices, setTypeChoices] = useState<string[]>([]);
  const [projectChoicesLoaded, setProjectChoicesLoaded] = useState(false);
  const [crewTab] = useState<'planerat' | 'ejPlanerat' | 'perKund'>('planerat');
  const [mainTab, setMainTab] = useState<'projekt' | 'medarbetare'>('projekt');
  const [newProjectDialog, setNewProjectDialog] = useState<{ open: boolean; title: string; customerId: number | null; type: string; status: string; saljstatus: string; fte: number; autoFte: boolean; tidKvar: number; timmarVarjeVecka: number; loading: boolean }>({
    open: false, title: '', customerId: null, type: '', status: '', saljstatus: '', fte: 0, autoFte: false, tidKvar: 0, timmarVarjeVecka: 0, loading: false
  });
  const [statusFilter, setStatusFilter] = useState<'pågående' | 'sälj' | 'alla'>('pågående');
  const [showOnlyMissingCoverage, setShowOnlyMissingCoverage] = useState(false);
  const [historyRange, setHistoryRange] = useState<'3m' | '6m' | 'all'>('3m');
  const [historicalHoursByProject, setHistoricalHoursByProject] = useState<{ [projectTitle: string]: number }>({});
  const [historicalEarliestDateByProject, setHistoricalEarliestDateByProject] = useState<{ [projectTitle: string]: number }>({});
  const [historicalAvailableHours, setHistoricalAvailableHours] = useState<number>(0);
  const [historicalMonthsInRange, setHistoricalMonthsInRange] = useState<number>(0);
  const [historicalHoursByProjectFirstHalf, setHistoricalHoursByProjectFirstHalf] = useState<{ [projectTitle: string]: number }>({});
  const [historicalHoursByProjectSecondHalf, setHistoricalHoursByProjectSecondHalf] = useState<{ [projectTitle: string]: number }>({});
  const [historyLoading, setHistoryLoading] = useState(false);

  // SharePoint internal name for "Säljstatus" (encoded ä)
  const SALJSTATUS_FIELD = 'S_x00e4_ljstatus';

  // Färgpalett för slumpmässiga färger - Material Design - helt unika grundfärger
  const colors = [
    '#F44336', // Röd
    '#E91E63', // Rosa
    '#9C27B0', // Lila
    '#673AB7', // Mörkviolett
    '#3F51B5', // Indigo
    '#2196F3', // Blå
    '#03A9F4', // Ljusblå
    '#00BCD4', // Cyan
    '#009688', // Teal
    '#4CAF50', // Grön
    '#8BC34A', // Ljusgrön
    '#CDDC39', // Lime
    '#FFEB3B', // Gul
    '#FFC107', // Amber
    '#FF9800', // Orange
    '#FF5722', // Djuporange
    '#795548', // Brun
    '#9E9E9E', // Grå
    '#607D8B', // Blågre
    '#455A64', // Mörkblågre
  ];

  // Garanterar unika färger inom en given uppsättning nycklar
  // (t.ex. kundboxarna i samma vecka-cell) genom att stega vidare i paletten vid kollision
  // (t.ex. kundboxarna i samma vecka-cell) genom att stega vidare i paletten vid kollision
  const getDistinctColorsForKeys = (keysInOrder: string[]): { [key: string]: string } => {
    const hashIndex = (text: string): number => {
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }
      return Math.abs(hash) % colors.length;
    };

    const used = new Set<number>();
    const result: { [key: string]: string } = {};
    keysInOrder.forEach((key) => {
      let index = hashIndex(key);
      let attempts = 0;
      while (used.has(index) && attempts < colors.length) {
        index = (index + 1) % colors.length;
        attempts++;
      }
      used.add(index);
      result[key] = colors[index];
    });
    return result;
  };

  // Heatmap background color based on total allocation %
  const getHeatmapColor = (pct: number): string => {
    if (pct === 0) return 'transparent';
    if (pct <= 50) return `rgba(0, 126, 134, ${0.04 + (pct / 50) * 0.07})`;
    if (pct <= 100) return `rgba(0, 126, 134, ${0.11 + ((pct - 50) / 50) * 0.1})`;
    if (pct <= 130) return `rgba(199, 69, 47, ${0.15 + ((pct - 100) / 30) * 0.15})`;
    return 'rgba(178, 54, 54, 0.28)';
  };

  // Funktion för att avgöra om en färg är mörk
  const isDarkColor = (color: string): boolean => {
    const darkColors = ['#F44336', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#009688', '#FF5722', '#795548', '#607D8B', '#455A64'];
    return darkColors.includes(color);
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchAllUsers();
      await fetchPlanningData();
      await fetchProjects();
      await fetchCustomerAnsvarig();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Laddar alla anv\u00e4ndare i tenanten f\u00f6rst n\u00e4r formul\u00e4ret \u00f6ppnas, s\u00e5 Namn-f\u00e4ltet kan f\u00f6resl\u00e5 vem som helst, inte bara redan planerade personer
  useEffect(() => {
    if (isModalOpen && !tenantUsersLoaded) {
      sharepointService.getAllTenantUsers().then((users) => {
        setTenantUsers(users);
        setTenantUsersLoaded(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const fetchAllUsers = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) return;

      // Fetch users from Tid list
      const response = await sharepointService.getListItems(
        '/Lists/Time',
        accessToken,
        { $select: 'Title' }
      );

      if (response.success && response.data) {
        const users = response.data
          .map((item: any) => item.Title)
          .filter((name: string) => name);
        
        const uniqueUsers = Array.from(new Set(users)).sort() as string[];
        setPersons(uniqueUsers);
        console.log('Loaded users from Tid list:', uniqueUsers);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Get current ISO 8601 week number (European standard, week starts on Monday)
  const getCurrentWeekNumber = (): number => {
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // ISO weekday: Mon=0, Sun=6
    const dayNr = (target.getDay() + 6) % 7;
    // Set to nearest Thursday (current date + 3 - weekday)
    target.setDate(target.getDate() - dayNr + 3);
    // January 4 is always in ISO week 1
    const jan4 = new Date(target.getFullYear(), 0, 4);
    const jan4DayNr = (jan4.getDay() + 6) % 7;
    // Get the Thursday of week 1
    const firstThursday = new Date(jan4.getFullYear(), 0, jan4.getDate() - jan4DayNr + 3);
    // Calculate week number
    const diff = target.getTime() - firstThursday.getTime();
    return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
  };

  // Get all remaining weeks of the year starting from current week
  const getAllWeeks = (): number[] => {
    const currentWeek = getCurrentWeekNumber();
    const allWeeks: number[] = [];
    // ISO weeks: most years have 52, some have 53
    const lastDay = new Date(new Date().getFullYear(), 11, 28);
    const dayNum = lastDay.getDay() || 7;
    lastDay.setDate(lastDay.getDate() + 4 - dayNum);
    const yearStart = new Date(lastDay.getFullYear(), 0, 1);
    const totalWeeks = Math.ceil((((lastDay.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    for (let w = currentWeek; w <= totalWeeks; w++) {
      allWeeks.push(w);
    }
    return allWeeks;
  };

  // Planerade timmar för ett projekt (nuvarande vecka och framåt)
  const getPlannedFutureHours = (projectTitle: string): number => {
    const currentWk = getCurrentWeekNumber();
    const futureWks = weeks.filter(w => w >= currentWk);
    let planned = 0;
    Object.keys(planningData).forEach(person => {
      futureWks.forEach(week => {
        (planningData[person]?.[week] || []).forEach(a => {
          if (a.project === projectTitle) planned += (a.percentage / 100) * 40;
        });
      });
    });
    return planned;
  };

  const getFutureWeeksCount = (): number => {
    const currentWk = getCurrentWeekNumber();
    return weeks.filter(w => w >= currentWk).length;
  };

  const getRemainingWeeksInYear = (): number => {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31);
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    return Math.max(1, Math.ceil((endOfYear.getTime() - now.getTime()) / msPerWeek));
  };

  // FTE-behov beräknat utifrån kvarvarande arbete (Dagar kvar/Timmar varje vecka), oavsett vad som redan är planerat.
  // Timmar varje vecka har alltid företräde om båda är satta; oberoende av projekttyp. Avrundas till en decimal.
  const calcAutoFte = (_projectTitle: string, tidKvarVal: number, timmarVarjeVeckaVal: number): number => {
    if (timmarVarjeVeckaVal > 0) {
      return Math.round((timmarVarjeVeckaVal / 40) * 10) / 10;
    }
    const remainingWeeks = getRemainingWeeksInYear();
    return tidKvarVal > 0 ? Math.round(((tidKvarVal * 8) / (40 * remainingWeeks)) * 10) / 10 : 0;
  };

  // Planerade/behövda timmar i jämförbar enhet, används både för täckning per projekt och aggregerat per kund
  const calcCoverageHours = (projectTitle: string, tidKvarVal: number, timmarVarjeVeckaVal: number, fteVal: number): { planned: number; target: number } | null => {
    const planned = getPlannedFutureHours(projectTitle);
    const futureWeeksCount = getFutureWeeksCount();
    if (timmarVarjeVeckaVal > 0) {
      return { planned, target: timmarVarjeVeckaVal * futureWeeksCount };
    }
    if (tidKvarVal > 0) {
      return { planned, target: tidKvarVal * 8 };
    }
    if (fteVal > 0 && futureWeeksCount > 0) {
      return { planned, target: fteVal * 40 * futureWeeksCount };
    }
    return null;
  };

  // Täckning: hur stor andel av behovet som redan är planerat
  const calcCoveragePercent = (projectTitle: string, tidKvarVal: number, timmarVarjeVeckaVal: number, fteVal: number): number => {
    const stats = calcCoverageHours(projectTitle, tidKvarVal, timmarVarjeVeckaVal, fteVal);
    if (!stats || stats.target <= 0) return 0;
    return Math.min(Math.round((stats.planned / stats.target) * 100), 999);
  };

  // Antal arbetsdagar en månads semester motsvarar, dras bort från tillgänglig arbetstid
  const VACATION_WORKDAYS_PER_MONTH = 21;

  // Påskdagen (Meeus/Jones/Butcher-algoritmen), grund för de rörliga röda dagarna
  const getEasterSunday = (year: number): Date => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  // Svenska röda dagar för ett givet år (Midsommardagen/Alla helgons dag utelämnade - infaller alltid på lördag)
  const getSwedishHolidays = (year: number): Date[] => {
    const easter = getEasterSunday(year);
    const addDays = (date: Date, days: number): Date => {
      const d = new Date(date);
      d.setDate(d.getDate() + days);
      return d;
    };
    return [
      new Date(year, 0, 1),
      new Date(year, 0, 6),
      addDays(easter, -2),
      easter,
      addDays(easter, 1),
      new Date(year, 4, 1),
      addDays(easter, 39),
      addDays(easter, 49),
      new Date(year, 5, 6),
      new Date(year, 11, 25),
      new Date(year, 11, 26)
    ];
  };

  // Antal vardagar (ej lördag/söndag/röd dag) mellan två datum, inklusive start- och slutdatum
  const countSwedishWorkdays = (start: Date, end: Date): number => {
    const holidaysByYear: { [year: number]: Set<string> } = {};
    let count = 0;
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (cur <= last) {
      const year = cur.getFullYear();
      if (!holidaysByYear[year]) {
        holidaysByYear[year] = new Set(getSwedishHolidays(year).map(d => d.toDateString()));
      }
      const weekday = cur.getDay();
      if (weekday !== 0 && weekday !== 6 && !holidaysByYear[year].has(cur.toDateString())) {
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  // Hämtar historisk tid (Time-listan) per projekt för vald period, används för historik-kolumnen
  const fetchHistoricalTime = async (range: '3m' | '6m' | 'all') => {
    try {
      setHistoryLoading(true);
      const accessToken = await getAccessToken();
      if (!accessToken) return;

      const today = new Date();
      let startDate: Date | null = null;
      let filterClause: string | undefined;
      if (range !== 'all') {
        startDate = new Date();
        if (range === '3m') startDate.setMonth(startDate.getMonth() - 3);
        else if (range === '6m') startDate.setMonth(startDate.getMonth() - 6);
        const cutoffStr = startDate.toISOString().split('T')[0];
        filterClause = `Date ge datetime'${cutoffStr}T00:00:00Z'`;
      }

      const response = await sharepointService.getListItems(
        '/Lists/Time',
        accessToken,
        {
          $select: 'Hours,Date,Project/Title',
          $expand: 'Project',
          $filter: filterClause,
          $top: 5000
        }
      );

      if (response.success && response.data) {
        const items = response.data as any[];
        const hoursByProject: { [projectTitle: string]: number } = {};
        const earliestDateByProject: { [projectTitle: string]: number } = {};
        let earliestMs = today.getTime();
        items.forEach((item) => {
          const title = item.Project?.Title;
          if (!title) return;
          hoursByProject[title] = (hoursByProject[title] || 0) + (parseFloat(item.Hours) || 0);
          const dateMs = item.Date ? new Date(item.Date).getTime() : NaN;
          if (!isNaN(dateMs) && dateMs < earliestMs) earliestMs = dateMs;
          if (!isNaN(dateMs) && (earliestDateByProject[title] === undefined || dateMs < earliestDateByProject[title])) {
            earliestDateByProject[title] = dateMs;
          }
        });
        setHistoricalHoursByProject(hoursByProject);
        setHistoricalEarliestDateByProject(earliestDateByProject);

        const rangeStart = startDate || new Date(earliestMs);
        const workdays = countSwedishWorkdays(rangeStart, today);
        const availableWorkdays = Math.max(1, workdays - VACATION_WORKDAYS_PER_MONTH);
        setHistoricalAvailableHours(availableWorkdays * 8);

        const rangeDays = Math.max(1, (today.getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000));
        setHistoricalMonthsInRange(Math.max(1, rangeDays / 30.44));

        // Delar upp perioden i två hälfter för att avgöra om nedlagd tid ökar/minskar
        const midpointMs = rangeStart.getTime() + (today.getTime() - rangeStart.getTime()) / 2;
        const firstHalf: { [projectTitle: string]: number } = {};
        const secondHalf: { [projectTitle: string]: number } = {};
        items.forEach((item) => {
          const title = item.Project?.Title;
          if (!title) return;
          const dateMs = item.Date ? new Date(item.Date).getTime() : NaN;
          if (isNaN(dateMs)) return;
          // Juli (semestermånad) ska inte påverka trendriktningen
          if (new Date(dateMs).getMonth() === 6) return;
          const bucket = dateMs < midpointMs ? firstHalf : secondHalf;
          bucket[title] = (bucket[title] || 0) + (parseFloat(item.Hours) || 0);
        });
        setHistoricalHoursByProjectFirstHalf(firstHalf);
        setHistoricalHoursByProjectSecondHalf(secondHalf);
      }
    } catch (err) {
      console.error('Fel vid hämtning av historisk tid:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // FTE historiskt: nedlagd tid i perioden omräknat till en genomsnittlig FTE, tillgänglig tid minus en månads semester och röda dagar
  const getHistoricalFte = (projectTitle: string): number => {
    if (historicalAvailableHours <= 0) return 0;
    const hours = historicalHoursByProject[projectTitle] || 0;
    return Math.round((hours / historicalAvailableHours) * 10) / 10;
  };

  // Genomsnittligt antal timmar per månad i perioden
  const getHistoricalMonthlyHours = (projectTitle: string): number => {
    if (historicalMonthsInRange <= 0) return 0;
    const hours = historicalHoursByProject[projectTitle] || 0;
    return Math.round(hours / historicalMonthsInRange);
  };

  // Jämför periodens första och andra hälft för att avgöra trend
  const getHistoricalTrend = (projectTitle: string): 'up' | 'flat' | 'down' => {
    const first = historicalHoursByProjectFirstHalf[projectTitle] || 0;
    const second = historicalHoursByProjectSecondHalf[projectTitle] || 0;
    if (first === 0 && second === 0) return 'flat';
    if (first === 0) return 'up';
    const change = (second - first) / first;
    if (change > 0.1) return 'up';
    if (change < -0.1) return 'down';
    return 'flat';
  };

  // Vänligt formaterat datum för "Uppdaterad"-kolumnen, t.ex. "För 10 minuter sen" / "Igår kl. 14:32"
  const formatFriendlyDate = (dateStr?: string | null): string => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / (60 * 1000));
    const timeStr = date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    if (diffMin < 1) return 'Just nu';
    if (diffMin < 60) return `För ${diffMin} minut${diffMin === 1 ? '' : 'er'} sen`;
    const isSameDay = date.toDateString() === now.toDateString();
    if (isSameDay) return `Idag kl. ${timeStr}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return `Igår kl. ${timeStr}`;
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (diffDays < 7) return `För ${diffDays} dag${diffDays === 1 ? '' : 'ar'} sen`;
    return date.toLocaleDateString('sv-SE');
  };

  // Trendpilen visas bara om projektet har registrerad tid minst tre månader bakåt
  const hasSufficientHistoryForTrend = (projectTitle: string): boolean => {
    const earliestMs = historicalEarliestDateByProject[projectTitle];
    if (earliestMs === undefined) return false;
    const threeMonthsMs = 90 * 24 * 60 * 60 * 1000;
    return Date.now() - earliestMs >= threeMonthsMs;
  };

  useEffect(() => {
    if (statusFilter === 'pågående') {
      fetchHistoricalTime(historyRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, historyRange]);


  // Hämtar kundansvarig (Ansvarig-kolumnen på Kund-listan) för visning bredvid kunden i projekttabellen
  const fetchCustomerAnsvarig = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }

      const response = await sharepointService.getListItems(
        '/Lists/Customer',
        accessToken,
        {
          $select: 'Title,Ansvarig/Title',
          $expand: 'Ansvarig',
          $top: 1000
        }
      );

      if (response.success && response.data) {
        const map: { [customerTitle: string]: string } = {};
        response.data.forEach((c: any) => {
          if (c.Title && c.Ansvarig?.Title) {
            map[c.Title] = c.Ansvarig.Title;
          }
        });
        setCustomerAnsvarig(map);
      }
    } catch (err) {
      console.error('Fel vid hämtning av kundansvarig:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }

      const response = await sharepointService.getListItems(
        '/Lists/Projects',
        accessToken,
        { 
          $select: `Id,Title,Customer/Id,Customer/Title,Tidkvar,Status,Type,TimmarVarjeVecka,FTE,PlanningUpdated,${SALJSTATUS_FIELD}`,
          $expand: 'Customer'
        }
      );

      if (response.success && response.data) {
        setAllProjects(response.data);
        
        // Extract unique customers and sort
        const uniqueCustomers = Array.from(
          new Map(
            response.data
              .filter((p: any) => p.Customer?.Title)
              .map((p: any) => [p.Customer.Title, p.Customer])
          ).values()
        ).sort((a: any, b: any) => a.Title.localeCompare(b.Title, 'sv'));
        
        setCustomers(uniqueCustomers);
      }
    } catch (err) {
      console.error('Fel vid hämtning av projekt:', err);
    }
  };

  // Hämtar valbara alternativ för Status/Säljstatus (Choice-fält) från listan, en gång
  const ensureProjectFieldChoices = async () => {
    if (projectChoicesLoaded) return;
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) return;
      const schema = await sharepointService.getListSchema('/Lists/Projects', accessToken);
      if (schema.success && schema.data) {
        const statusField = schema.data.find((f: any) => f.InternalName === 'Status');
        const saljstatusField = schema.data.find((f: any) => f.InternalName === SALJSTATUS_FIELD);
        const typeField = schema.data.find((f: any) => f.InternalName === 'Type');
        setStatusChoices(statusField?.Choices || []);
        setSaljstatusChoices(saljstatusField?.Choices || []);
        setTypeChoices(typeField?.Choices || []);
      }
    } catch (err) {
      console.warn('Kunde inte hämta fältval för projekt:', err);
    } finally {
      setProjectChoicesLoaded(true);
    }
  };

  const openProjectEditDialog = (proj: { id: number; title: string; status: string; saljstatus: string; type: string; fte: number; tidKvar: number; timmarVarjeVecka: number }) => {
    setProjectEditDialog({
      open: true,
      projectId: proj.id,
      projectTitle: proj.title,
      status: proj.status,
      saljstatus: proj.saljstatus,
      type: proj.type,
      fte: proj.fte,
      autoFte: proj.timmarVarjeVecka > 0 || proj.tidKvar > 0,
      tidKvar: proj.tidKvar,
      timmarVarjeVecka: proj.timmarVarjeVecka,
      loading: false
    });
    ensureProjectFieldChoices();
  };

  const handleDeleteProject = async () => {
    if (!deleteProjectDialog) return;
    const { projectId } = deleteProjectDialog;
    try {
      setDeleteProjectDialog(prev => prev ? { ...prev, loading: true } : prev);
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Ingen åtkomsttoken tillgänglig');
        return;
      }
      await sharepointService.deleteListItem('/Lists/Projects', projectId, accessToken);
      setAllProjects(prev => prev.filter((p: any) => p.Id !== projectId));
      setDeleteProjectDialog(null);
      setProjectEditDialog(prev => ({ ...prev, open: false }));
    } catch (err) {
      console.error('Fel vid borttagning av projekt:', err);
      setError('Kunde inte ta bort projekt');
      setDeleteProjectDialog(prev => prev ? { ...prev, loading: false } : prev);
    }
  };

  const openNewProjectDialog = () => {
    setNewProjectDialog({
      open: true, title: '', customerId: null, type: '', status: '', saljstatus: '',
      fte: 0, autoFte: false, tidKvar: 0, timmarVarjeVecka: 0, loading: false
    });
    ensureProjectFieldChoices();
  };

  const handleCustomerChange = (customerName: string) => {
    setFormData({...formData, customer: customerName, projects: [{ project: '', percentage: 100 }]});
    
    // Filter projects by selected customer
    if (customerName) {
      const filtered = allProjects
        .filter((p: any) => p.Customer?.Title === customerName)
        .sort((a: any, b: any) => a.Title.localeCompare(b.Title, 'sv'));
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects([]);
    }
  };

  const addProjectRow = () => {
    setFormData({ ...formData, projects: [...formData.projects, { project: '', percentage: 100 }] });
  };

  const removeProjectRow = (idx: number) => {
    setFormData({ ...formData, projects: formData.projects.filter((_, i) => i !== idx) });
  };

  const updateProjectRow = (idx: number, patch: Partial<{ project: string; percentage: number }>) => {
    setFormData({ ...formData, projects: formData.projects.map((p, i) => i === idx ? { ...p, ...patch } : p) });
  };

  const handlePersonInputChange = (value: string) => {
    setFormData({ ...formData, person: value });

    // Namn kan väljas bland både redan planerade personer och alla användare i tenanten
    const allNames = Array.from(new Set([...persons, ...tenantUsers])).sort((a, b) => a.localeCompare(b, 'sv'));

    // Filter available people based on input, show all if empty
    if (value.length > 0) {
      const filtered = allNames.filter(p => 
        p.toLowerCase().includes(value.toLowerCase())
      );
      setPersonSuggestions(filtered);
    } else {
      // Show all persons when input is empty
      setPersonSuggestions(allNames);
    }
  };

  const handleSelectPerson = (personName: string) => {
    setFormData({ ...formData, person: personName });
    setPersonSuggestions([]);
  };

  // Öppnar redigeringsformuläret för en hel kund-grupp (kan innehålla flera projekt för samma person/vecka)
  const handleEditGroup = (person: string, week: number, group: { allocation: any; idx: number }[]) => {
    setEditingItem({ person, week, items: group });
    setPersonSuggestions([]);

    const first = group[0].allocation;
    const sourceWeek = first?.sourceWeek ?? week;
    const isAllWeeks = sourceWeek === 0;
    const isUnavailable = first.project === 'Ej tillgänglig';

    if (isUnavailable) {
      setFormData({
        person,
        selectedWeeks: [isAllWeeks ? 0 : sourceWeek],
        customer: '',
        projects: [{ project: '', percentage: first.percentage }],
        isUnavailable: true
      });
      setIsModalOpen(true);
      return;
    }

    const customerName = first.customer || '';
    const projectRows = group.map(({ allocation }) => {
      // Match both name AND customer to handle duplicate project names
      const project = allProjects.find((p: any) => p.Title === allocation.project && p.Customer?.Title === allocation.customer);
      return { project: project ? String(project.Id) : '', percentage: allocation.percentage };
    });

    setFormData({
      person,
      selectedWeeks: [isAllWeeks ? 0 : sourceWeek],
      customer: customerName,
      projects: projectRows.length > 0 ? projectRows : [{ project: '', percentage: 100 }],
      isUnavailable: false
    });

    // Filter projects for this customer
    if (customerName) {
      const filtered = allProjects
        .filter((p: any) => p.Customer?.Title === customerName)
        .sort((a: any, b: any) => a.Title.localeCompare(b.Title, 'sv'));
      setFilteredProjects(filtered);
    }

    setIsModalOpen(true);
  };

  const handleDelete = (person: string, week: number, index: number) => {
    const allocation = planningData[person]?.[week]?.[index];
    if (!allocation) return;
    setDeleteDialog({ open: true, person, week, index, allocation });
  };

  const handleDeleteSingle = async () => {
    if (!deleteDialog) return;
    const { person, week, index, allocation } = deleteDialog;
    setDeleteDialog(null);

    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      if (!accessToken) { setError('Ingen åtkomsttoken tillgänglig'); return; }

      const isAllWeeksItem = allocation.sourceWeek === 0;
      const isUnavailable = allocation.project === 'Ej tillgänglig';

      const filterCondition = isUnavailable
        ? `Person/Title eq '${person}' and (Week eq ${week} or Week eq 0) and Title eq 'Ej tillgänglig'`
        : `Person/Title eq '${person}' and (Week eq ${week} or Week eq 0) and Project/Title eq '${allocation.project}'`;

      const response = await sharepointService.getListItems('/Lists/Planning', accessToken, { $filter: filterCondition });

      if (response.success && response.data && response.data.length > 0) {
        const itemId = response.data[0].Id;
        await sharepointService.deleteListItem('/Lists/Planning', itemId, accessToken);

        const newData = { ...planningData };
        if (isAllWeeksItem) {
          Object.keys(newData[person]).forEach((w) => {
            const weekKey = Number(w);
            newData[person][weekKey] = newData[person][weekKey].filter(
              (a: any) => !(a.sourceWeek === 0 && a.project === allocation.project)
            );
            if (newData[person][weekKey].length === 0) delete newData[person][weekKey];
          });
        } else {
          newData[person][week].splice(index, 1);
          if (newData[person][week].length === 0) delete newData[person][week];
        }
        setPlanningData(newData);
      }
    } catch (err) {
      console.error('Fel vid borttagning:', err);
      setError('Kunde inte ta bort planeringsraden');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllWeeks = async () => {
    if (!deleteDialog) return;
    const { person, week, allocation } = deleteDialog;
    setDeleteDialog(null);

    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      if (!accessToken) { setError('Ingen åtkomsttoken tillgänglig'); return; }

      const isUnavailable = allocation.project === 'Ej tillgänglig';

      // Find ALL matching items for this person + project across all weeks (from current week onward)
      const filterCondition = isUnavailable
        ? `Person/Title eq '${person}' and Week ge ${week} and Title eq 'Ej tillgänglig'`
        : `Person/Title eq '${person}' and Week ge ${week} and Project/Title eq '${allocation.project}'`;

      const response = await sharepointService.getListItems('/Lists/Planning', accessToken, { $filter: filterCondition });

      if (response.success && response.data && response.data.length > 0) {
        // Delete all matching items in parallel
        await Promise.all(response.data.map((item: any) =>
          sharepointService.deleteListItem('/Lists/Planning', item.Id, accessToken)
            .then(() => console.log('Deleted planning item:', item.Id, 'week:', item.Week))
            .catch((err: any) => console.error('Error deleting item', item.Id, ':', err))
        ));
      }

      // Refresh data
      await fetchPlanningData();
    } catch (err) {
      console.error('Fel vid borttagning:', err);
      setError('Kunde inte ta bort planeringsraderna');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.person) {
      setError('Fyll i alla fält');
      return;
    }
    if (!formData.isUnavailable && (!formData.customer || formData.projects.some(p => !p.project))) {
      setError('Fyll i alla fält');
      return;
    }
    if (formData.selectedWeeks.length === 0) {
      setError('Välj minst en vecka');
      return;
    }

    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Ingen åtkomsttoken tillgänglig');
        return;
      }

      // Resolve the person's ID from their display name
      const personId = await sharepointService.resolvePersonField(formData.person);
      if (!personId) {
        setError('Kunde inte hitta användare: ' + formData.person);
        return;
      }

      const rows = formData.isUnavailable ? [formData.projects[0]] : formData.projects;

      if (editingItem) {
        // Redigering — samma veckoval som vid tillägg (en, flera eller alla veckor)
        const existingItems: { allocation: any; idx: number }[] = editingItem.items;
        const originalWeek = editingItem.week;
        const selectedWeeks = formData.selectedWeeks;
        const otherWeeks = selectedWeeks.filter(w => w !== originalWeek);
        const ops: Promise<any>[] = [];

        rows.forEach((row, i) => {
          const itemData: any = {
            Percentage: row.percentage,
            PersonId: personId
          };
          if (formData.isUnavailable) {
            itemData.Title = 'Ej tillgänglig';
            itemData.ProjectId = null;
          } else {
            const projectId = parseInt(row.project);
            if (projectId) itemData.ProjectId = projectId;
          }

          const existing = existingItems[i];
          const existingId = existing?.allocation?.Id || existing?.allocation?.id;

          if (existing) {
            const isUnavailableRow = existing.allocation.project === 'Ej tillgänglig';

            // Ursprungsveckan: uppdatera om den fortfarande är vald, annars ta bort
            if (existingId) {
              if (selectedWeeks.includes(originalWeek)) {
                ops.push(
                  sharepointService.updateListItem('/Lists/Planning', existingId, { ...itemData, Week: originalWeek }, accessToken)
                    .catch((err) => { console.error('Error updating item:', err); setError('Kunde inte uppdatera planeringsraden'); })
                );
              } else {
                ops.push(
                  sharepointService.deleteListItem('/Lists/Planning', existingId, accessToken)
                    .catch((err) => console.error('Error deleting item', existingId, ':', err))
                );
              }
            }

            // Övriga valda veckor: hitta matchande rad eller skapa ny
            if (otherWeeks.length > 0) {
              const weekFilter = otherWeeks.map(w => `Week eq ${w}`).join(' or ');
              const filterCondition = isUnavailableRow
                ? `Person/Title eq '${formData.person}' and (${weekFilter}) and Title eq 'Ej tillgänglig'`
                : `Person/Title eq '${formData.person}' and (${weekFilter}) and Project/Title eq '${existing.allocation.project}'`;

              ops.push(
                sharepointService.getListItems('/Lists/Planning', accessToken, { $filter: filterCondition })
                  .then((response) => {
                    const foundByWeek = new Map<number, number>();
                    if (response.success && response.data) {
                      response.data.forEach((item: any) => foundByWeek.set(item.Week, item.Id));
                    }
                    return Promise.all(otherWeeks.map((w) => {
                      const foundId = foundByWeek.get(w);
                      return foundId
                        ? sharepointService.updateListItem('/Lists/Planning', foundId, { ...itemData, Week: w }, accessToken)
                            .catch((err: any) => console.error('Error updating item', foundId, ':', err))
                        : sharepointService.createListItem('/Lists/Planning', { ...itemData, Week: w }, accessToken)
                            .catch((err: any) => console.error('Error creating item for week', w, ':', err));
                    }));
                  })
                  .catch((err) => console.error('Error fetching items for weeks:', err))
              );
            }
          } else {
            // Ny rad som lagts till i formuläret — skapa för alla valda veckor
            selectedWeeks.forEach((w) => {
              ops.push(
                sharepointService.createListItem('/Lists/Planning', { ...itemData, Week: w }, accessToken)
                  .catch((err) => { console.error('Error creating item:', err); setError('Kunde inte skapa planeringsraden'); })
              );
            });
          }
        });

        // Rader (projekt) som togs bort helt i formuläret
        for (let i = rows.length; i < existingItems.length; i++) {
          const existing = existingItems[i];
          const id = existing?.allocation?.Id || existing?.allocation?.id;
          if (id) {
            ops.push(
              sharepointService.deleteListItem('/Lists/Planning', id, accessToken)
                .catch((err) => console.error('Error deleting item', id, ':', err))
            );
          }
        }

        await Promise.all(ops);
      } else {
        // Add new — create one row per selected week per projekt (parallel for speed)
        const createPromises: Promise<any>[] = [];
        formData.selectedWeeks.forEach((weekValue) => {
          rows.forEach((row) => {
            const itemData: any = {
              Week: weekValue,
              Percentage: row.percentage,
              PersonId: personId
            };

            if (formData.isUnavailable) {
              itemData.Title = 'Ej tillgänglig';
            } else {
              const projectId = parseInt(row.project);
              if (projectId) {
                itemData.ProjectId = projectId;
              }
            }

            createPromises.push(
              sharepointService.createListItem('/Lists/Planning', itemData, accessToken)
                .then((result) => {
                  if (!result.success) throw new Error(result.error || 'Failed to create');
                  console.log('Item created for week', weekValue);
                })
                .catch((err) => {
                  console.error('Error creating item for week', weekValue, ':', err);
                  setError('Kunde inte skapa planeringsraden för vecka ' + weekValue);
                })
            );
          });
        });

        await Promise.all(createPromises);
      }

      setIsModalOpen(false);
      // Refresh data after save
      await fetchPlanningData();
    } catch (err) {
      console.error('Fel vid sparning:', err);
      setError('Kunde inte spara planeringsraden');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanningData = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        setError('Ingen åtkomsttoken tillgänglig');
        setLoading(false);
        return;
      }

      const response = await sharepointService.getListItems(
        '/Lists/Planning',
        accessToken,
        {
          $select: 'Id,Title,Week,Project/Id,Project/Title,Person/Title,Percentage',
          $expand: 'Person,Project',
          $orderby: 'Week asc',
          $top: 5000
        }
      );

      if (response.success && response.data) {
        const items = response.data as any[];
        
        // Collect unique project IDs to fetch their customer info
        const projectIds = new Set<number>();
        const projectMap: { [key: number]: any } = {};
        
        items.forEach((item) => {
          if (item.Project?.Id) {
            projectIds.add(item.Project.Id);
          }
        });

        // Fetch project details for all unique projects
        if (projectIds.size > 0) {
          const projectIdArray = Array.from(projectIds);
          const projectFilter = projectIdArray.map(id => `Id eq ${id}`).join(' or ');
          
          try {
            const projectsResponse = await sharepointService.getListItems(
              '/Lists/Projects',
              accessToken,
              { 
                $select: 'Id,Title,Customer/Title',
                $expand: 'Customer',
                $filter: projectFilter
              }
            );
            
            if (projectsResponse.success && projectsResponse.data) {
              projectsResponse.data.forEach((proj: any) => {
                projectMap[proj.Id] = proj;
              });
            }
          } catch (err) {
            console.warn('Could not fetch project details:', err);
          }
        }
        
        // Group data by person and week
        const grouped: PlanningData = {};
        const weekSet = new Set<number>();
        const personSet = new Set<string>();

        items.forEach((item) => {
          const person = item.Person?.Title || 'Okänd';
          const week = item.Week || 0;
          const isUnavailable = item.Title === 'Ej tillgänglig';
          const project = isUnavailable ? 'Ej tillgänglig' : (item.Project?.Title || '');
          const projectId = item.Project?.Id;
          const customer = isUnavailable ? '' : (projectId && projectMap[projectId]?.Customer?.Title ? projectMap[projectId].Customer.Title : '');
          const percentage = item.Percentage || 0;
          const isAllWeeks = week === 0;
          const id = item.Id;
          
          console.log('Item data:', { id, person, week, project, projectId, customer, percentage, isAllWeeks, fullItem: item });

          personSet.add(person);
          if (week !== 0) {
            weekSet.add(week);
          }

          if (!grouped[person]) {
            grouped[person] = {};
          }

          // If isAllWeeks, add to all upcoming weeks
          if (isAllWeeks) {
            getAllWeeks().forEach((w: number) => {
              if (!grouped[person][w]) {
                grouped[person][w] = [];
              }
              grouped[person][w].push({
                Id: id,
                sourceWeek: week,
                project,
                customer,
                percentage
              });
            });
          } else {
            if (!grouped[person][week]) {
              grouped[person][week] = [];
            }
            grouped[person][week].push({
              Id: id,
              sourceWeek: week,
              project,
              customer,
              percentage
            });
          }
        });

        setPlanningData(grouped);
        // Merge computed week range with any weeks that have data
        const computedWeeks = getAllWeeks();
        const allWeekNumbers = new Set([...computedWeeks, ...Array.from(weekSet)]);
        setWeeks(Array.from(allWeekNumbers).sort((a, b) => a - b));
        setPersons(Array.from(personSet).sort());
      } else {
        setError('Kunde inte hämta planeringsdata');
      }
    } catch (err) {
      console.error('Fel vid hämtning av planeringsdata:', err);
      setError('Ett fel uppstod vid hämtning av data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="planning-container"><div className="loading">Laddar planeringsdata...</div></div>;
  }

  if (error) {
    return (
      <div className="planning-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchPlanningData} className="retry-button">
          Försök igen
        </button>
      </div>
    );
  }

  // Filter weeks based on view mode
  const currentWeek = getCurrentWeekNumber();
  const displayedWeeks = viewMode === 'twoMonths'
    ? weeks.filter(w => w >= currentWeek && w < currentWeek + 9)
    : weeks;

  return (
    <div className="planning-container">
      <div className="main-tabs">
        <button
          className={`main-tab-btn ${mainTab === 'projekt' ? 'active' : ''}`}
          onClick={() => setMainTab('projekt')}
        >
          Projekt
        </button>
        <button
          className={`main-tab-btn ${mainTab === 'medarbetare' ? 'active' : ''}`}
          onClick={() => setMainTab('medarbetare')}
        >
          Medarbetare
        </button>
      </div>
      {mainTab === 'medarbetare' && (
        <>
      <div className="planning-header">
        {/* Togglen Planerat/Ej planerat/Per kund borttagen, kan återinföras vid behov
        <div className="crew-tabs">
          <button
            className={`crew-tab-btn ${crewTab === 'planerat' ? 'active' : ''}`}
            onClick={() => setCrewTab('planerat')}
          >
            Planerat
          </button>
          <button
            className={`crew-tab-btn ${crewTab === 'ejPlanerat' ? 'active' : ''}`}
            onClick={() => setCrewTab('ejPlanerat')}
          >
            Ej planerat
          </button>
          <button
            className={`crew-tab-btn ${crewTab === 'perKund' ? 'active' : ''}`}
            onClick={() => setCrewTab('perKund')}
          >
            Per kund
          </button>
        </div>
        */}
        <div className="header-controls">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'twoMonths' ? 'active' : ''}`}
              onClick={() => setViewMode('twoMonths')}
            >
              2 månader
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'fullYear' ? 'active' : ''}`}
              onClick={() => setViewMode('fullYear')}
            >
              Hela året
            </button>
          </div>
          <button
            className="add-button"
            onClick={() => {
              setFormData({ person: '', selectedWeeks: [currentWeek], customer: '', projects: [{ project: '', percentage: 100 }], isUnavailable: false });
              setEditingItem(null);
              setIsModalOpen(true);
            }}
          >
            + Lägg till
          </button>
        </div>
      </div>
      {persons.length === 0 ? (
        <div className="no-data">Ingen planeringsdata tillgänglig</div>
      ) : (
        <div className={`planning-table-wrapper ${viewMode === 'twoMonths' ? 'view-two-months' : 'view-full-year'}`}>
          <table className="planning-table">
            <thead>
              <tr>
                <th className="person-column">
                  {crewTab === 'planerat' ? 'Namn' : crewTab === 'perKund' ? 'Kund' : 'Kund'}
                </th>
                {displayedWeeks.map((week) => (
                  <th key={week} className={`week-column${week === currentWeek ? ' current-week-col' : ''}`}>
                    {week === currentWeek && <div className="current-week-dot" />}
                    <span className="week-label">V</span>{week}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {crewTab === 'planerat' && persons.sort((a, b) => {
                let aTotal = 0;
                let bTotal = 0;
                weeks.forEach(week => {
                  const aAllocations = planningData[a]?.[week] || [];
                  const bAllocations = planningData[b]?.[week] || [];
                  aTotal += aAllocations.reduce((sum, al) => sum + (al.percentage || 0), 0);
                  bTotal += bAllocations.reduce((sum, al) => sum + (al.percentage || 0), 0);
                });
                return bTotal - aTotal; // Descending order (highest first)
              }).map((person) => (
                <tr key={person}>
                  <td className="person-column">
                    <span className="person-name-text" title={person}>{person}</span>
                  </td>
                  {displayedWeeks.map((week) => {
                    const weekAllocations = planningData[person]?.[week] || [];
                    const totalPercentage = weekAllocations.reduce((sum, a) => sum + (a.percentage || 0), 0);
                    return (
                    <td key={`${person}-${week}`} className={`week-cell${week === currentWeek ? ' current-week-col' : ''}`} style={{ backgroundColor: getHeatmapColor(totalPercentage) }}>
                      <div className="cell-content">
                        {totalPercentage > 0 && (
                          <div className={`week-total-percentage${totalPercentage > 100 ? ' over-allocated' : ''}`}>{totalPercentage}%</div>
                        )}
                        {(() => {
                          const pairs = (planningData[person]?.[week] || []).map((allocation, idx) => ({ allocation, idx }));
                          const unavailablePairs = pairs.filter(p => p.allocation.project === 'Ej tillgänglig');
                          const otherPairs = pairs.filter(p => p.allocation.project !== 'Ej tillgänglig');

                          const groups: { [key: string]: { allocation: any; idx: number }[] } = {};
                          const order: string[] = [];
                          otherPairs.forEach((pair) => {
                            const key = pair.allocation.customer || pair.allocation.project;
                            if (!groups[key]) { groups[key] = []; order.push(key); }
                            groups[key].push(pair);
                          });
                          order.sort((a, b) => {
                            const totalA = groups[a].reduce((s, p) => s + (p.allocation.percentage || 0), 0);
                            const totalB = groups[b].reduce((s, p) => s + (p.allocation.percentage || 0), 0);
                            return totalB - totalA;
                          });
                          const boxColors = getDistinctColorsForKeys(order);

                          return (
                            <>
                              {unavailablePairs.map(({ allocation, idx }) => (
                                <div
                                  key={`u-${idx}`}
                                  className="allocation"
                                  style={{ backgroundColor: 'var(--color-text-muted)', color: 'var(--color-text-inverse)' }}
                                  title="Ej tillgänglig"
                                >
                                  <div className="allocation-text">
                                    Ej tillgänglig
                                    {allocation.percentage > 0 && (
                                      <div className="allocation-percentage">{allocation.percentage}%</div>
                                    )}
                                  </div>
                                  <div className="allocation-actions">
                                    <button
                                      className="action-btn edit-btn"
                                      onClick={() => handleEditGroup(person, week, [{ allocation, idx }])}
                                      title="Redigera"
                                    >
                                      ✎
                                    </button>
                                    <button
                                      className="action-btn delete-btn"
                                      onClick={() => handleDelete(person, week, idx)}
                                      title="Ta bort"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              ))}
                              {order.map((key) => {
                                const group = groups[key];
                                const customerLabel = group[0].allocation.customer || group[0].allocation.project;
                                const totalPct = group.reduce((s, p) => s + (p.allocation.percentage || 0), 0);
                                const baseColor = boxColors[key];
                                const bgColor = totalPct >= 100 ? '#007e86' : baseColor;
                                const textColor = totalPct >= 100 || isDarkColor(bgColor) ? '#FFFFFF' : '#262420';
                                return (
                                  <div
                                    key={key}
                                    className="allocation customer-box"
                                    style={{ backgroundColor: bgColor, color: textColor }}
                                    title={customerLabel}
                                  >
                                    <div className="customer-box-header">
                                      <span className="customer-box-name">{customerLabel.substring(0, 16)}</span>
                                      {totalPct > 0 && <span className="customer-box-total">{totalPct}%</span>}
                                    </div>
                                    <div className="customer-box-projects">
                                      {group.map(({ allocation, idx }) => (
                                        <div key={idx} className="customer-box-project-row">
                                          <span className="project-line-text" title={allocation.project}>
                                            {allocation.project}{allocation.percentage > 0 ? ` · ${allocation.percentage}%` : ''}
                                          </span>
                                          <span className="allocation-actions">
                                            <button
                                              className="action-btn delete-btn"
                                              onClick={() => handleDelete(person, week, idx)}
                                              title="Ta bort"
                                            >
                                              ✕
                                            </button>
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="allocation-actions">
                                      <button
                                        className="action-btn edit-btn"
                                        onClick={() => handleEditGroup(person, week, group)}
                                        title="Redigera"
                                      >
                                        ✎
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    );
                  })}
                </tr>
              ))}

              {/* Per-kund-vy och Ej planerat-vy borttagna tillsammans med togglen, kan återinföras vid behov
              {crewTab === 'perKund' && (() => {
                const custMap: { [cust: string]: { [week: number]: { person: string; project: string; percentage: number }[] } } = {};
                persons.forEach(person => {
                  displayedWeeks.forEach(week => {
                    (planningData[person]?.[week] || []).forEach(a => {
                      const cust = a.customer || 'Utan kund';
                      if (!custMap[cust]) custMap[cust] = {};
                      if (!custMap[cust][week]) custMap[cust][week] = [];
                      custMap[cust][week].push({ person, project: a.project, percentage: a.percentage });
                    });
                  });
                });
                const custList = Object.keys(custMap).sort();
                if (custList.length === 0) return <tr><td colSpan={displayedWeeks.length + 1} className="no-data">Ingen planeringsdata</td></tr>;
                return custList.map(cust => (
                  <tr key={cust}>
                    <td className="person-column">
                      <span className="person-name-text" title={cust}>{cust}</span>
                    </td>
                    {displayedWeeks.map(week => {
                      const allocs = custMap[cust]?.[week] || [];
                      return (
                        <td key={week} className={`week-cell${week === currentWeek ? ' current-week-col' : ''}`}>
                          <div className="cell-content">
                            {allocs.map((a, idx) => {
                              const bg = getConsistentColor(a.person);
                              const tc = isDarkColor(bg) ? '#fff' : '#262420';
                              return (
                                <div key={idx} className="allocation" style={{ backgroundColor: bg, color: tc }}
                                  title={`${a.person} — ${a.project} (${a.percentage}%)`}>
                                  <div className="allocation-text">
                                    {a.person.split(' ')[0]}
                                    <div className="allocation-percentage">{a.percentage}%</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}

              {crewTab === 'ejPlanerat' && allProjects.length > 0 && (() => {
                const curWeek = getCurrentWeekNumber();
                const twoMonthWeeks = weeks.filter(w => w >= curWeek && w < curWeek + 9);
                const spreadWeeks = Math.max(1, twoMonthWeeks.length);

                // Beräkna ej inplanerade timmar per projekt
                interface UnplannedProject { project: string; customer: string; byWeek: { [week: number]: number }; totalUnplanned: number; }
                const unplannedList: UnplannedProject[] = [];

                allProjects.forEach((p: any) => {
                  const timmarVarjeVecka = parseFloat(p.TimmarVarjeVecka) || 0;
                  const tidKvar = parseFloat(p.Tidkvar) || 0;

                  if (timmarVarjeVecka <= 0 && tidKvar <= 0) return;

                  const weeklyTarget = timmarVarjeVecka > 0 ? timmarVarjeVecka : (tidKvar / spreadWeeks);
                  const byWeek: { [week: number]: number } = {};

                  twoMonthWeeks.forEach(week => {
                    let plannedThisWeek = 0;
                    Object.keys(planningData).forEach(person => {
                      const allocations = planningData[person]?.[week] || [];
                      allocations.forEach(a => {
                        if (a.project === p.Title && a.customer === (p.Customer?.Title || '')) {
                          plannedThisWeek += (a.percentage / 100) * 40;
                        }
                      });
                    });

                    const unplannedHours = weeklyTarget - plannedThisWeek;
                    if (unplannedHours > 0.5) {
                      byWeek[week] = Math.round(unplannedHours);
                    }
                  });

                  const totalUnplanned = Object.values(byWeek).reduce((s, h) => s + h, 0);
                  if (totalUnplanned > 0) {
                    unplannedList.push({ project: p.Title, customer: p.Customer?.Title || '', byWeek, totalUnplanned });
                  }
                });

                // Gruppera per kund
                const customerMap: { [customer: string]: UnplannedProject[] } = {};
                unplannedList.forEach(entry => {
                  const cust = entry.customer || 'Okänd kund';
                  if (!customerMap[cust]) customerMap[cust] = [];
                  customerMap[cust].push(entry);
                });

                // Sortera kunder efter totala ej planerade timmar (högst först)
                const customerEntries = Object.entries(customerMap)
                  .map(([customer, projects]) => ({
                    customer,
                    projects: projects.sort((a, b) => b.totalUnplanned - a.totalUnplanned),
                    totalUnplanned: projects.reduce((s, p) => s + p.totalUnplanned, 0)
                  }))
                  .sort((a, b) => b.totalUnplanned - a.totalUnplanned);

                if (customerEntries.length === 0) return null;

                return customerEntries.map(custEntry => (
                  <tr key={`unplanned-cust-${custEntry.customer}`} className="unplanned-row">
                    <td className="person-column unplanned-label" title={custEntry.customer}>
                      {custEntry.customer}
                    </td>
                    {displayedWeeks.map(week => {
                      const isInRange = twoMonthWeeks.includes(week);
                      // Samla alla projekt för denna kund med ej planerade timmar denna vecka
                      const projectsThisWeek = isInRange
                        ? custEntry.projects.filter(p => (p.byWeek[week] || 0) > 0)
                        : [];
                      const totalHours = projectsThisWeek.reduce((s, p) => s + (p.byWeek[week] || 0), 0);
                      return (
                        <td key={`unplanned-${custEntry.customer}-${week}`} className="week-cell">
                          <div className="cell-content">
                            {totalHours > 0 && <div className="week-total-percentage unplanned-total">{totalHours}h</div>}
                            {projectsThisWeek.map((proj, idx) => {
                              const colorKey = `${custEntry.customer} - ${proj.project}`;
                              const hours = proj.byWeek[week] || 0;
                              const bgColor = getConsistentColor(colorKey);
                              const textColor = isDarkColor(bgColor) ? '#FFFFFF' : '#262420';
                              return (
                                <div
                                  key={idx}
                                  className="allocation unplanned-allocation"
                                  style={{ backgroundColor: bgColor, color: textColor, opacity: 0.7 }}
                                  title={`${custEntry.customer} - ${proj.project} — ${hours}h ej planerat`}
                                >
                                  <div className="allocation-text">
                                    {proj.project.substring(0, 14)}
                                    <div className="allocation-percentage">{hours}h</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}
              */}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}

      {/* Projektplanering */}
      {mainTab === 'projekt' && (() => {
        const baseProjectRows = (allProjects.length > 0 && weeks.length > 0) ? allProjects
          .map((p: any) => {
            const tidKvar = parseFloat(p.Tidkvar) || 0;
            const timmarVarjeVecka = parseFloat(p.TimmarVarjeVecka) || 0;
            const fte = parseFloat(p.FTE) || 0;
            const hasTidKvarBasis = timmarVarjeVecka > 0 || tidKvar > 0;
            return {
              id: p.Id,
              title: p.Title || '',
              customer: p.Customer?.Title || '',
              status: p.Status || '',
              type: p.Type || '',
              saljstatus: p[SALJSTATUS_FIELD] || '',
              tidKvar,
              timmarVarjeVecka,
              planningUpdated: p.PlanningUpdated || null,
              hasTidKvarBasis,
              // Om Dagar kvar/Timmar varje vecka är satt räknas FTE fram live istället för att lita på sparat värde
              fte: hasTidKvarBasis
                ? calcAutoFte(p.Title, tidKvar, timmarVarjeVecka)
                : fte,
              hasCoverageBasis: hasTidKvarBasis || fte > 0,
              coverage: calcCoveragePercent(p.Title, tidKvar, timmarVarjeVecka, fte),
              coverageHours: calcCoverageHours(p.Title, tidKvar, timmarVarjeVecka, fte)
            };
          })
          .filter(p => statusFilter === 'alla' ? !p.status.toLowerCase().includes('avslutad') : p.status.toLowerCase() === statusFilter)
          .filter(p => !(p.customer === 'Ignia' && ['internt', 'sälj'].includes(p.title.toLowerCase())))
          .sort((a, b) => {
            const aActive = a.status.toLowerCase() === 'pågående' ? 1 : 0;
            const bActive = b.status.toLowerCase() === 'pågående' ? 1 : 0;
            if (aActive !== bActive) return bActive - aActive;
            return a.title.localeCompare(b.title, 'sv');
          }) : [];

        // "Visa bara de där täckning saknas" filtrerar bara vilka rader som visas i tabellen,
        // den ska inte påverka kundtotalens täckning/FTE (de räknas alltid på alla rader nedan)
        const projectRows = baseProjectRows.filter(p => !showOnlyMissingCoverage || (p.hasCoverageBasis && p.coverage <= 90));

        const futureWeeksCountTotal = getFutureWeeksCount();
        // FTE som ännu inte är inplanerad, givet behovet (fte) och redan inplanerade timmar (coverageHours.planned)
        const calcMissingFte = (fteVal: number, coverageHoursVal: { planned: number; target: number } | null): number => {
          if (!coverageHoursVal) return 0;
          const plannedFte = futureWeeksCountTotal > 0 ? coverageHoursVal.planned / (40 * futureWeeksCountTotal) : 0;
          return Math.max(0, Math.round((fteVal - plannedFte) * 10) / 10);
        };

        const customerGroups: { customer: string; rows: typeof baseProjectRows; displayRows: typeof baseProjectRows; hasCoverageBasis: boolean; coverage: number; fte: number; missingFte: number }[] = [];
        baseProjectRows.forEach(proj => {
          let group = customerGroups.find(g => g.customer === proj.customer);
          if (!group) {
            group = { customer: proj.customer, rows: [], displayRows: [], hasCoverageBasis: false, coverage: 0, fte: 0, missingFte: 0 };
            customerGroups.push(group);
          }
          group.rows.push(proj);
        });
        projectRows.forEach(proj => {
          const group = customerGroups.find(g => g.customer === proj.customer);
          if (group) group.displayRows.push(proj);
        });
        // Täckning/FTE på kundnivå: räknas alltid på alla kundens projekt (opåverkat av "täckning saknas"-kryssrutan),
        // visas även om något av kundens projekt saknar underlag (döljs bara om inget projekt har underlag)
        customerGroups.forEach(group => {
          const hasAnyBasis = group.rows.some(r => r.coverageHours);
          const plannedSum = group.rows.reduce((s, r) => s + (r.coverageHours?.planned || 0), 0);
          const targetSum = group.rows.reduce((s, r) => s + (r.coverageHours?.target || 0), 0);
          group.hasCoverageBasis = hasAnyBasis && targetSum > 0;
          group.coverage = group.hasCoverageBasis ? Math.min(Math.round((plannedSum / targetSum) * 100), 999) : 0;
          group.fte = Math.round(group.rows.reduce((s, r) => s + r.fte, 0) * 10) / 10;
          const groupPlannedFte = futureWeeksCountTotal > 0 ? plannedSum / (40 * futureWeeksCountTotal) : 0;
          group.missingFte = Math.max(0, Math.round((group.fte - groupPlannedFte) * 10) / 10);
        });
        // Kunder utan synliga rader (allt bortfiltrerat av "täckning saknas") visas inte i tabellen
        const visibleCustomerGroups = customerGroups.filter(group => !showOnlyMissingCoverage || group.displayRows.length > 0);
        // Kunder med mest bemanning (summerad FTE) högst upp
        visibleCustomerGroups.sort((a, b) => {
          if (a.fte !== b.fte) return b.fte - a.fte;
          return a.customer.localeCompare(b.customer, 'sv');
        });

        const showHistoryColumn = statusFilter === 'pågående';
        // Kolumner före FTE (Projekt + Projekttyp/Säljstatus/Status) respektive mellan FTE och Täckning (Dagar kvar + ev. Historik)
        const groupColSpanBeforeFte = 2;
        const groupColSpanAfterFte = 1 + (showHistoryColumn ? 1 : 0);

        // Total inplanerad/saknad FTE över alla projekt (samma bas som täckning per kund, opåverkat av "täckning saknas"-kryssrutan)
        const totalNeededFteRaw = baseProjectRows.reduce((s, r) => s + r.fte, 0);
        const totalPlannedHours = baseProjectRows.reduce((s, r) => s + (r.coverageHours?.planned || 0), 0);
        const totalPlannedFteRaw = futureWeeksCountTotal > 0 ? totalPlannedHours / (40 * futureWeeksCountTotal) : 0;
        const totalPlannedFte = Math.round(totalPlannedFteRaw * 10) / 10;
        const totalMissingFte = Math.max(0, Math.round((totalNeededFteRaw - totalPlannedFteRaw) * 10) / 10);

        return (
          <div className="uncovered-projects-section">
            <div className="uncovered-section-header">
              <div>
                <h3 className="uncovered-title">Projekt</h3>
                <p className="uncovered-subtitle">Status, bemanning (FTE) och täckning per projekt ({statusFilter === 'alla' ? 'ej avslutade' : statusFilter})</p>
              </div>
              <div className="header-fte-summary">
                <div className="header-fte-stat">
                  <span className="header-fte-stat-label">Inplanerad FTE</span>
                  <span className="header-fte-stat-value">{totalPlannedFte || '—'}</span>
                </div>
                <div className="header-fte-stat">
                  <span className="header-fte-stat-label">Saknad FTE</span>
                  <span className={`header-fte-stat-value ${totalMissingFte > 0 ? 'header-fte-stat-missing' : ''}`}>{totalMissingFte ? totalMissingFte.toFixed(1) : '—'}</span>
                </div>
              </div>
              <div className="uncovered-header-actions">
                <div className="filter-group">
                  <span className="filter-group-label">Status</span>
                  <div className="status-filter-toggle">
                    {(['pågående', 'sälj', 'alla'] as const).map(opt => (
                      <button
                        key={opt}
                        className={`status-filter-btn ${statusFilter === opt ? 'active' : ''}`}
                        onClick={() => setStatusFilter(opt)}
                      >
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                {statusFilter === 'pågående' && (
                  <div className="filter-group">
                    <span className="filter-group-label">Historik</span>
                    <div className="history-controls">
                      {(['3m', '6m', 'all'] as const).map(opt => (
                        <button
                          key={opt}
                          type="button"
                          className={`history-range-btn ${historyRange === opt ? 'active' : ''}`}
                          onClick={() => setHistoryRange(opt)}
                          disabled={historyLoading}
                        >
                          {opt === '3m' ? '3 mån' : opt === '6m' ? 'Halvår' : 'Allt'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <label className="coverage-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={showOnlyMissingCoverage}
                    onChange={(e) => setShowOnlyMissingCoverage(e.target.checked)}
                  />
                  Visa bara de där täckning i planering saknas
                </label>
                <button className="add-button" onClick={openNewProjectDialog}>+ Nytt projekt</button>
              </div>
            </div>
            {visibleCustomerGroups.length > 0 && (
            <div className="uncovered-table-wrapper">
              <table className="uncovered-table">
                <thead>
                  <tr>
                    <th>Projekt</th>
                    {statusFilter === 'pågående' && <th>Projekttyp</th>}
                    {statusFilter === 'sälj' && <th>Säljstatus</th>}
                    {statusFilter === 'alla' && <th>Status</th>}
                    <th className="num-col">FTE</th>
                    <th className="num-col">Dagar kvar/Timmar varje vecka</th>
                    {showHistoryColumn && (
                      <th className="num-col">
                        Historik
                        <span
                          className="info-icon"
                          title="Snitt antal timmar per månad och motsvarande FTE, baserat på registrerad tid i vald period. Visas bara om projektet har minst tre månaders rapporterad historik. Pilen visar om nedlagd tid ökar, är oförändrad eller minskar jämfört med periodens första hälft."
                        >
                          i
                        </span>
                      </th>
                    )}
                    <th className="num-col">Täckning i planering</th>
                    {showOnlyMissingCoverage && <th className="num-col">FTE Saknas</th>}
                    <th className="num-col">Uppdaterad</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCustomerGroups.map(group => (
                    <React.Fragment key={group.customer || '—'}>
                      <tr className="customer-group-row">
                        <td colSpan={groupColSpanBeforeFte} className="customer-group-cell">
                          {group.customer || 'Okänd kund'}
                          {customerAnsvarig[group.customer] && (
                            <span className="customer-group-ansvarig">{customerAnsvarig[group.customer]}</span>
                          )}
                        </td>
                        <td className="num-col customer-group-fte">{group.fte || '—'}</td>
                        <td colSpan={groupColSpanAfterFte} className="customer-group-cell"></td>
                        <td className="num-col">
                          {group.hasCoverageBasis ? (
                            <div className={`coverage-bar-container ${group.coverage < 100 ? 'coverage-low' : ''}`}>
                              <div className="coverage-bar-track">
                                <div
                                  className={`coverage-bar ${group.coverage >= 100 ? 'coverage-bar-full' : 'coverage-bar-low'}`}
                                  style={{ width: `${Math.min(group.coverage, 100)}%` }}
                                />
                              </div>
                              <span className="coverage-text">{group.coverage}%</span>
                            </div>
                          ) : (
                            <span className="coverage-none">—</span>
                          )}
                        </td>
                        {showOnlyMissingCoverage && (
                          <td className="num-col customer-group-fte">{group.missingFte || '—'}</td>
                        )}
                        <td className="num-col"></td>
                      </tr>
                      {group.displayRows.map((proj, idx) => (
                        <tr
                          key={idx}
                          className="project-row-editable"
                          title="Klicka för att redigera projekt"
                          onClick={() => openProjectEditDialog(proj)}
                        >
                          <td>{proj.title}</td>
                          {statusFilter === 'pågående' && <td>{proj.type || '—'}</td>}
                          {statusFilter === 'sälj' && (
                            <td>
                              {proj.saljstatus ? (
                                <span className={`status-badge status-${proj.saljstatus.toLowerCase().replace(/\s+/g, '-')}`}>
                                  {proj.saljstatus}
                                </span>
                              ) : (
                                <span className="coverage-none">—</span>
                              )}
                            </td>
                          )}
                          {statusFilter === 'alla' && (
                            <td>
                              {proj.status ? (
                                <span className={`status-badge status-${proj.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                  {proj.status}
                                </span>
                              ) : (
                                <span className="coverage-none">—</span>
                              )}
                            </td>
                          )}
                          <td className="num-col">{proj.fte || '—'}</td>
                          <td className="num-col">
                            {proj.timmarVarjeVecka > 0
                              ? <>{proj.timmarVarjeVecka} h <span className="tid-kvar-suffix">(varje vecka)</span></>
                              : proj.tidKvar > 0
                                ? `${proj.tidKvar} d`
                                : '—'
                            }
                          </td>
                          {showHistoryColumn && (
                            <td className="num-col">
                              {(() => {
                                // Kräver minst tre månaders rapporterad historik, annars visas inget värde alls
                                if (!hasSufficientHistoryForTrend(proj.title)) return '—';
                                const monthlyHours = getHistoricalMonthlyHours(proj.title);
                                const fte = getHistoricalFte(proj.title);
                                if (!monthlyHours && !fte) return '—';
                                const trend = getHistoricalTrend(proj.title);
                                const trendArrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
                                return (
                                  <span className="history-cell">
                                    {monthlyHours}h/mån ({fte})
                                    <span className={`trend-arrow trend-${trend}`}>{trendArrow}</span>
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          <td className="num-col">
                            {proj.hasCoverageBasis ? (
                              <div className={`coverage-bar-container ${proj.coverage < 100 ? 'coverage-low' : ''}`}>
                                <div className="coverage-bar-track">
                                  <div
                                    className={`coverage-bar ${proj.coverage >= 100 ? 'coverage-bar-full' : 'coverage-bar-low'}`}
                                    style={{ width: `${Math.min(proj.coverage, 100)}%` }}
                                  />
                                </div>
                                <span className="coverage-text">{proj.coverage}%</span>
                              </div>
                            ) : (
                              <span className="coverage-none">—</span>
                            )}
                          </td>
                          {showOnlyMissingCoverage && (
                            <td className="num-col">{proj.hasCoverageBasis ? (calcMissingFte(proj.fte, proj.coverageHours) || '—') : '—'}</td>
                          )}
                          <td className="num-col">{formatFriendlyDate(proj.planningUpdated)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );
      })()}

      {/* Gantt-tidslinje (Projekttidslinje) borttagen, kan återinföras vid behov
      {mainTab === 'medarbetare' && planningData && Object.keys(planningData).length > 0 && (() => {
        const ganttWeeks = weeks.filter(w => w >= currentWeek && w < currentWeek + 14);
        if (ganttWeeks.length === 0) return null;

        // Build per-project weekly hours from planningData
        const projWeekMap: { [proj: string]: { customer: string; byWeek: { [w: number]: number } } } = {};
        Object.keys(planningData).forEach(person => {
          ganttWeeks.forEach(w => {
            (planningData[person]?.[w] || []).forEach(a => {
              if (a.project === 'Ej tillgänglig') return;
              if (!projWeekMap[a.project]) projWeekMap[a.project] = { customer: a.customer || '', byWeek: {} };
              projWeekMap[a.project].byWeek[w] = (projWeekMap[a.project].byWeek[w] || 0) + Math.round((a.percentage / 100) * 40);
            });
          });
        });

        const entries = Object.entries(projWeekMap).sort((a, b) => {
          const aSum = Object.values(a[1].byWeek).reduce((s, h) => s + h, 0);
          const bSum = Object.values(b[1].byWeek).reduce((s, h) => s + h, 0);
          return bSum - aSum;
        });
        if (entries.length === 0) return null;

        return (
          <div className="gantt-section">
            <div className="gantt-header-section">
              <h3 className="gantt-title">Projekttidslinje</h3>
              <p className="gantt-subtitle">Planerade timmar per projekt och vecka (närmaste 14 veckor)</p>
            </div>
            <div className="gantt-table-wrapper">
              <table className="gantt-table">
                <thead>
                  <tr>
                    <th className="gantt-project-col">Projekt</th>
                    {ganttWeeks.map(w => (
                      <th key={w} className={`gantt-week-col${w === currentWeek ? ' current-week-col' : ''}`}>
                        {w === currentWeek && <div className="current-week-dot" />}
                        <span className="week-label">V</span>{w}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entries.map(([proj, data]) => {
                    const barColor = getConsistentColor(`${data.customer} - ${proj}`);
                    const textColor = isDarkColor(barColor) ? '#fff' : '#262420';
                    return (
                      <tr key={proj}>
                        <td className="gantt-project-col" title={`${data.customer} — ${proj}`}>
                          <div className="gantt-project-name">{proj}</div>
                          {data.customer && <div className="gantt-customer-name">{data.customer}</div>}
                        </td>
                        {ganttWeeks.map(w => {
                          const h = data.byWeek[w] || 0;
                          return (
                            <td key={w} className={`gantt-week-cell${w === currentWeek ? ' current-week-col' : ''}`}>
                              {h > 0 && (
                                <div className="gantt-bar" style={{ backgroundColor: barColor, color: textColor }}>
                                  {h}h
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      */}

      {/* Modal för redigera projekt (Status/Säljstatus/FTE) */}
      {projectEditDialog.open && (
        <div className="planning-modal-overlay" onClick={() => setProjectEditDialog(prev => ({ ...prev, open: false }))}>
          <div className="planning-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Redigera projekt – {projectEditDialog.projectTitle}</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                setProjectEditDialog(prev => ({ ...prev, loading: true }));
                const accessToken = await getAccessToken();
                if (!accessToken) {
                  setError('Ingen åtkomsttoken tillgänglig');
                  return;
                }
                const saljstatusValue = projectEditDialog.status === 'Sälj' ? projectEditDialog.saljstatus : null;
                const fteLocked = projectEditDialog.tidKvar > 0 || projectEditDialog.timmarVarjeVecka > 0;
                const computedFte = fteLocked
                  ? calcAutoFte(projectEditDialog.projectTitle, projectEditDialog.tidKvar, projectEditDialog.timmarVarjeVecka)
                  : projectEditDialog.fte;
                const newTidKvar = fteLocked ? projectEditDialog.tidKvar : 0;
                const newTimmarVarjeVecka = fteLocked ? projectEditDialog.timmarVarjeVecka : 0;
                const planningUpdatedNow = new Date().toISOString();
                const updateFields: any = {
                  Title: projectEditDialog.projectTitle,
                  Status: projectEditDialog.status,
                  Type: projectEditDialog.type,
                  FTE: computedFte,
                  Tidkvar: newTidKvar,
                  TimmarVarjeVecka: newTimmarVarjeVecka,
                  PlanningUpdated: planningUpdatedNow,
                  [SALJSTATUS_FIELD]: saljstatusValue
                };
                await sharepointService.updateListItem(
                  '/Lists/Projects',
                  projectEditDialog.projectId,
                  updateFields,
                  accessToken
                );
                setAllProjects(prev => prev.map((p: any) =>
                  p.Id === projectEditDialog.projectId
                    ? {
                        ...p,
                        Title: projectEditDialog.projectTitle,
                        Status: projectEditDialog.status,
                        Type: projectEditDialog.type,
                        FTE: computedFte,
                        Tidkvar: updateFields.Tidkvar,
                        TimmarVarjeVecka: updateFields.TimmarVarjeVecka,
                        [SALJSTATUS_FIELD]: saljstatusValue,
                        Modified: new Date().toISOString(),
                        PlanningUpdated: planningUpdatedNow
                      }
                    : p
                ));
                setProjectEditDialog(prev => ({ ...prev, open: false, loading: false }));
              } catch (err) {
                console.error('Fel vid uppdatering av projekt:', err);
                setError('Kunde inte uppdatera projekt');
                setProjectEditDialog(prev => ({ ...prev, loading: false }));
              }
            }}>
              <div className="form-group">
                <label htmlFor="edit-project-title">Projekt:</label>
                <input
                  id="edit-project-title"
                  type="text"
                  value={projectEditDialog.projectTitle}
                  onChange={(e) => setProjectEditDialog(prev => ({ ...prev, projectTitle: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-project-status">Status:</label>
                <select
                  id="edit-project-status"
                  value={projectEditDialog.status}
                  onChange={(e) => setProjectEditDialog(prev => ({ ...prev, status: e.target.value }))}
                  required
                >
                  <option value="">Välj status</option>
                  {statusChoices.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {projectEditDialog.status && !statusChoices.includes(projectEditDialog.status) && (
                    <option value={projectEditDialog.status}>{projectEditDialog.status}</option>
                  )}
                </select>
              </div>
              {projectEditDialog.status === 'Sälj' && (
                <div className="form-group">
                  <label htmlFor="edit-project-saljstatus">Säljstatus:</label>
                  <select
                    id="edit-project-saljstatus"
                    value={projectEditDialog.saljstatus}
                    onChange={(e) => setProjectEditDialog(prev => ({ ...prev, saljstatus: e.target.value }))}
                  >
                    <option value="">Välj säljstatus</option>
                    {saljstatusChoices.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    {projectEditDialog.saljstatus && !saljstatusChoices.includes(projectEditDialog.saljstatus) && (
                      <option value={projectEditDialog.saljstatus}>{projectEditDialog.saljstatus}</option>
                    )}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="edit-project-type">Projekttyp:</label>
                <select
                  id="edit-project-type"
                  value={projectEditDialog.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setProjectEditDialog(prev => ({ ...prev, type: newType }));
                  }}
                  required
                >
                  <option value="">Välj projekttyp</option>
                  {(typeChoices.length > 0 ? typeChoices : ['Projekt', 'Förvaltning']).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {projectEditDialog.type && !typeChoices.includes(projectEditDialog.type) && !['Projekt', 'Förvaltning'].includes(projectEditDialog.type) && (
                    <option value={projectEditDialog.type}>{projectEditDialog.type}</option>
                  )}
                </select>
              </div>
              {(() => {
                // FTE låses (kan ej redigeras manuellt) så länge Dagar kvar/Timmar varje vecka har ett värde, oavsett projekttyp
                const fteLocked = projectEditDialog.tidKvar > 0 || projectEditDialog.timmarVarjeVecka > 0;
                return (
                  <>
                    <div className="form-group">
                      <label htmlFor="edit-project-fte">FTE:</label>
                      <div className="fte-stepper">
                        <button
                          type="button"
                          className="fte-step-btn"
                          disabled={fteLocked}
                          onClick={() => setProjectEditDialog(prev => ({ ...prev, autoFte: false, fte: Math.max(0, Math.round((prev.fte - 0.5) * 2) / 2) }))}
                        >
                          −
                        </button>
                        <input
                          id="edit-project-fte"
                          type="number"
                          min="0"
                          step="0.5"
                          disabled={fteLocked}
                          value={fteLocked
                            ? calcAutoFte(projectEditDialog.projectTitle, projectEditDialog.tidKvar, projectEditDialog.timmarVarjeVecka)
                            : projectEditDialog.fte}
                          onChange={(e) => setProjectEditDialog(prev => ({ ...prev, autoFte: false, fte: parseFloat(e.target.value) || 0 }))}
                          className="fte-input"
                        />
                        <button
                          type="button"
                          className="fte-step-btn"
                          disabled={fteLocked}
                          onClick={() => setProjectEditDialog(prev => ({ ...prev, autoFte: false, fte: Math.round((prev.fte + 0.5) * 2) / 2 }))}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="fte-or-divider">eller</div>
                    <div className="form-group">
                      <label htmlFor="edit-project-timmar">Timmar varje vecka:</label>
                      <input
                        id="edit-project-timmar"
                        type="number"
                        min="0"
                        step="1"
                        value={projectEditDialog.timmarVarjeVecka}
                        onChange={(e) => setProjectEditDialog(prev => ({ ...prev, autoFte: true, timmarVarjeVecka: parseFloat(e.target.value) || 0, tidKvar: 0 }))}
                      />
                    </div>
                    <div className="fte-or-divider">eller</div>
                    <div className="form-group">
                      <label htmlFor="edit-project-tidkvar">Dagar kvar:</label>
                      <input
                        id="edit-project-tidkvar"
                        type="number"
                        min="0"
                        step="0.5"
                        value={projectEditDialog.tidKvar}
                        onChange={(e) => setProjectEditDialog(prev => ({ ...prev, autoFte: true, tidKvar: parseFloat(e.target.value) || 0, timmarVarjeVecka: 0 }))}
                      />
                      {fteLocked && (
                        <span className="fte-auto-preview">
                          Beräknat FTE-behov: <strong>{calcAutoFte(projectEditDialog.projectTitle, projectEditDialog.tidKvar, projectEditDialog.timmarVarjeVecka)}</strong>
                        </span>
                      )}
                    </div>
                  </>
                );
              })()}
              <div className="form-actions">
                <button type="submit" className="btn-save" disabled={projectEditDialog.loading}>
                  {projectEditDialog.loading ? 'Sparar...' : 'Spara'}
                </button>
                <button type="button" className="btn-cancel" onClick={() => setProjectEditDialog(prev => ({ ...prev, open: false }))}>Avbryt</button>
              </div>
              <button
                type="button"
                className="btn-delete-project"
                onClick={() => setDeleteProjectDialog({ open: true, projectId: projectEditDialog.projectId, projectTitle: projectEditDialog.projectTitle, loading: false })}
              >
                Ta bort projekt
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bekräftelsedialog för att ta bort projekt */}
      {deleteProjectDialog && deleteProjectDialog.open && (
        <div className="planning-modal-overlay" onClick={() => setDeleteProjectDialog(null)}>
          <div className="planning-modal delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Ta bort projekt</h3>
            <div className="delete-confirm-content">
              <p>
                Vill du verkligen ta bort <strong>{deleteProjectDialog.projectTitle}</strong>?
              </p>
              <p className="delete-confirm-question">Detta går inte att ångra.</p>
            </div>
            <div className="delete-confirm-actions">
              <button className="btn-delete-single" onClick={handleDeleteProject} disabled={deleteProjectDialog.loading}>
                {deleteProjectDialog.loading ? 'Tar bort...' : 'Ja, ta bort'}
              </button>
              <button className="btn-cancel" onClick={() => setDeleteProjectDialog(null)} disabled={deleteProjectDialog.loading}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal för nytt projekt */}
      {newProjectDialog.open && (() => {
        return (
          <div className="planning-modal-overlay" onClick={() => setNewProjectDialog(prev => ({ ...prev, open: false }))}>
            <div className="planning-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Nytt projekt</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  setNewProjectDialog(prev => ({ ...prev, loading: true }));
                  const accessToken = await getAccessToken();
                  if (!accessToken) {
                    setError('Ingen åtkomsttoken tillgänglig');
                    return;
                  }
                  const fteLockedNew = newProjectDialog.tidKvar > 0 || newProjectDialog.timmarVarjeVecka > 0;
                  const computedFte = fteLockedNew
                    ? calcAutoFte(newProjectDialog.title, newProjectDialog.tidKvar, newProjectDialog.timmarVarjeVecka)
                    : newProjectDialog.fte;
                  const itemData: any = {
                    Title: newProjectDialog.title,
                    Type: newProjectDialog.type,
                    Status: newProjectDialog.status,
                    FTE: computedFte,
                    Tidkvar: fteLockedNew ? newProjectDialog.tidKvar : 0,
                    TimmarVarjeVecka: fteLockedNew ? newProjectDialog.timmarVarjeVecka : 0,
                    PlanningUpdated: new Date().toISOString(),
                    [SALJSTATUS_FIELD]: newProjectDialog.status === 'Sälj' ? newProjectDialog.saljstatus : null
                  };
                  if (newProjectDialog.customerId) {
                    itemData.CustomerId = newProjectDialog.customerId;
                  }
                  await sharepointService.createListItem('/Lists/Projects', itemData, accessToken);
                  await fetchProjects();
                  setNewProjectDialog(prev => ({ ...prev, open: false, loading: false }));
                } catch (err) {
                  console.error('Fel vid skapande av projekt:', err);
                  setError('Kunde inte skapa projekt');
                  setNewProjectDialog(prev => ({ ...prev, loading: false }));
                }
              }}>
                <div className="form-group">
                  <label htmlFor="new-project-title">Projekt:</label>
                  <input
                    id="new-project-title"
                    type="text"
                    value={newProjectDialog.title}
                    onChange={(e) => setNewProjectDialog(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-project-customer">Kund:</label>
                  <select
                    id="new-project-customer"
                    value={newProjectDialog.customerId ?? ''}
                    onChange={(e) => setNewProjectDialog(prev => ({ ...prev, customerId: e.target.value ? parseInt(e.target.value) : null }))}
                    required
                  >
                    <option value="">Välj kund</option>
                    {customers.map((cust) => (
                      <option key={cust.Id} value={cust.Id}>{cust.Title}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="new-project-type">Projekttyp:</label>
                  <select
                    id="new-project-type"
                    value={newProjectDialog.type}
                    onChange={(e) => setNewProjectDialog(prev => ({ ...prev, type: e.target.value }))}
                    required
                  >
                    <option value="">Välj projekttyp</option>
                    {(typeChoices.length > 0 ? typeChoices : ['Projekt', 'Förvaltning']).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="new-project-status">Status:</label>
                  <select
                    id="new-project-status"
                    value={newProjectDialog.status}
                    onChange={(e) => setNewProjectDialog(prev => ({ ...prev, status: e.target.value }))}
                    required
                  >
                    <option value="">Välj status</option>
                    {statusChoices.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                {newProjectDialog.status === 'Sälj' && (
                  <div className="form-group">
                    <label htmlFor="new-project-saljstatus">Säljstatus:</label>
                    <select
                      id="new-project-saljstatus"
                      value={newProjectDialog.saljstatus}
                      onChange={(e) => setNewProjectDialog(prev => ({ ...prev, saljstatus: e.target.value }))}
                    >
                      <option value="">Välj säljstatus</option>
                      {saljstatusChoices.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="new-project-fte">FTE:</label>
                  <div className="fte-stepper">
                    <button
                      type="button"
                      className="fte-step-btn"
                      disabled={newProjectDialog.tidKvar > 0 || newProjectDialog.timmarVarjeVecka > 0}
                      onClick={() => setNewProjectDialog(prev => ({ ...prev, autoFte: false, fte: Math.max(0, Math.round((prev.fte - 0.5) * 2) / 2) }))}
                    >
                      −
                    </button>
                    <input
                      id="new-project-fte"
                      type="number"
                      min="0"
                      step="0.5"
                      disabled={newProjectDialog.tidKvar > 0 || newProjectDialog.timmarVarjeVecka > 0}
                      value={(newProjectDialog.tidKvar > 0 || newProjectDialog.timmarVarjeVecka > 0)
                        ? calcAutoFte(newProjectDialog.title, newProjectDialog.tidKvar, newProjectDialog.timmarVarjeVecka)
                        : newProjectDialog.fte}
                      onChange={(e) => setNewProjectDialog(prev => ({ ...prev, autoFte: false, fte: parseFloat(e.target.value) || 0 }))}
                      className="fte-input"
                    />
                    <button
                      type="button"
                      className="fte-step-btn"
                      disabled={newProjectDialog.tidKvar > 0 || newProjectDialog.timmarVarjeVecka > 0}
                      onClick={() => setNewProjectDialog(prev => ({ ...prev, autoFte: false, fte: Math.round((prev.fte + 0.5) * 2) / 2 }))}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="fte-or-divider">eller</div>
                <div className="form-group">
                  <label htmlFor="new-project-timmar">Timmar varje vecka:</label>
                  <input
                    id="new-project-timmar"
                    type="number"
                    min="0"
                    step="1"
                    value={newProjectDialog.timmarVarjeVecka}
                    onChange={(e) => setNewProjectDialog(prev => ({ ...prev, autoFte: true, timmarVarjeVecka: parseFloat(e.target.value) || 0, tidKvar: 0 }))}
                  />
                </div>
                <div className="fte-or-divider">eller</div>
                <div className="form-group">
                  <label htmlFor="new-project-tidkvar">Dagar kvar:</label>
                  <input
                    id="new-project-tidkvar"
                    type="number"
                    min="0"
                    step="0.5"
                    value={newProjectDialog.tidKvar}
                    onChange={(e) => setNewProjectDialog(prev => ({ ...prev, autoFte: true, tidKvar: parseFloat(e.target.value) || 0, timmarVarjeVecka: 0 }))}
                  />
                  {(newProjectDialog.tidKvar > 0 || newProjectDialog.timmarVarjeVecka > 0) && (
                    <span className="fte-auto-preview">
                      Beräknat FTE-behov: <strong>{calcAutoFte(newProjectDialog.title, newProjectDialog.tidKvar, newProjectDialog.timmarVarjeVecka)}</strong>
                    </span>
                  )}
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-save" disabled={newProjectDialog.loading}>
                    {newProjectDialog.loading ? 'Sparar...' : 'Skapa projekt'}
                  </button>
                  <button type="button" className="btn-cancel" onClick={() => setNewProjectDialog(prev => ({ ...prev, open: false }))}>Avbryt</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {isModalOpen && (
        <div className="planning-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="planning-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingItem ? 'Redigera planering' : 'Lägg till planering'}</h3>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div className="form-group">
                <label htmlFor="allocation-person">Person:</label>
                <div className="person-input-container">
                  <input 
                    id="allocation-person"
                    type="text" 
                    value={formData.person}
                    onChange={(e) => handlePersonInputChange(e.target.value)}
                    onFocus={() => handlePersonInputChange(formData.person)}
                    placeholder="Skriv personens namn"
                    required
                  />
                  {personSuggestions.length > 0 && (
                    <div className="person-suggestions">
                      {personSuggestions.map((person, idx) => (
                        <div 
                          key={idx} 
                          className="suggestion-item"
                          onClick={() => handleSelectPerson(person)}
                        >
                          {person}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <fieldset className="form-group">
                <legend>Vecka:</legend>
                <div className="week-multi-select">
                  {(() => {
                    const addWeeks = weeks.filter(w => w >= currentWeek);
                    return (
                  <div className="week-multi-select-header">
                    <label className="week-select-all-label">
                      <input
                        type="checkbox"
                        checked={formData.selectedWeeks.length === addWeeks.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, selectedWeeks: [...addWeeks]});
                          } else {
                            setFormData({...formData, selectedWeeks: []});
                          }
                        }}
                      />
                      Välj alla ({formData.selectedWeeks.length}/{addWeeks.length})
                    </label>
                  </div>
                    );
                  })()}
                  <div className="week-multi-select-list">
                    {weeks.filter(w => w >= currentWeek).map((w) => (
                      <label key={w} className="week-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.selectedWeeks.includes(w)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, selectedWeeks: [...formData.selectedWeeks, w].sort((a, b) => a - b)});
                            } else {
                              setFormData({...formData, selectedWeeks: formData.selectedWeeks.filter(sw => sw !== w)});
                            }
                          }}
                        />
                        V{w}
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>
              {!formData.isUnavailable && (
                <>
                  <div className="form-group">
                    <label htmlFor="allocation-customer">Kund:</label>
                    <select 
                      id="allocation-customer"
                      value={formData.customer}
                      onChange={(e) => handleCustomerChange(e.target.value)}
                      required
                    >
                      <option value="">Välj kund</option>
                      {customers.map((cust) => (
                        <option key={cust.Title} value={cust.Title}>{cust.Title}</option>
                      ))}
                    </select>
                  </div>
                  <fieldset className="form-group">
                    <legend>Projekt:</legend>
                    <div className="project-rows">
                      {formData.projects.map((row, idx) => (
                        <div className="project-row" key={idx}>
                          <select
                            value={row.project}
                            onChange={(e) => updateProjectRow(idx, { project: e.target.value })}
                            disabled={!formData.customer}
                            required
                          >
                            <option value="">Välj projekt</option>
                            {filteredProjects.map((proj) => (
                              <option key={proj.Id} value={String(proj.Id)}>
                                {proj.Title}
                              </option>
                            ))}
                          </select>
                          <div className="percentage-slider-container project-row-percentage">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="5"
                              value={row.percentage}
                              onChange={(e) => updateProjectRow(idx, { percentage: parseInt(e.target.value) || 0 })}
                              className="percentage-slider"
                            />
                            <span className="percentage-value">{row.percentage}%</span>
                          </div>
                          {formData.projects.length > 1 && (
                            <button
                              type="button"
                              className="remove-project-row-btn"
                              onClick={() => removeProjectRow(idx)}
                              title="Ta bort projekt"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {formData.customer && filteredProjects.length > formData.projects.length && (
                      <button
                        type="button"
                        className="add-project-row-btn"
                        onClick={addProjectRow}
                      >
                        + Lägg till projekt
                      </button>
                    )}
                  </fieldset>
                </>
              )}
              {!editingItem && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isUnavailable}
                      onChange={(e) => setFormData({
                        ...formData,
                        isUnavailable: e.target.checked,
                        customer: e.target.checked ? '' : formData.customer,
                        projects: e.target.checked
                          ? [{ project: '', percentage: formData.projects[0]?.percentage || 100 }]
                          : formData.projects
                      })}
                    />
                    Ej tillgänglig/semester
                  </label>
                </div>
              )}
              {formData.isUnavailable && (
                <div className="form-group">
                  <label htmlFor="allocation-percentage">Procent (%):</label>
                  <div className="percentage-slider-container">
                    <input 
                      id="allocation-percentage"
                      type="range" 
                      min="0" 
                      max="100"
                      step="5"
                      value={formData.projects[0]?.percentage || 0}
                      onChange={(e) => updateProjectRow(0, { percentage: parseInt(e.target.value) || 0 })}
                      className="percentage-slider"
                    />
                    <span className="percentage-value">{formData.projects[0]?.percentage || 0}%</span>
                  </div>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn-save">Spara</button>
                <button type="button" className="btn-cancel" onClick={() => setIsModalOpen(false)}>Avbryt</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Delete confirmation dialog */}
      {deleteDialog && deleteDialog.open && (
        <div className="planning-modal-overlay" onClick={() => setDeleteDialog(null)}>
          <div className="planning-modal delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Ta bort planering</h3>
            <div className="delete-confirm-content">
              <p>
                <strong>{deleteDialog.allocation.project}</strong> för <strong>{deleteDialog.person}</strong>, vecka {deleteDialog.week}
                {deleteDialog.allocation.percentage > 0 && ` (${deleteDialog.allocation.percentage}%)`}
              </p>
              <p className="delete-confirm-question">Vad vill du ta bort?</p>
            </div>
            <div className="delete-confirm-actions">
              <button className="btn-delete-single" onClick={handleDeleteSingle}>
                Bara denna vecka
              </button>
              <button className="btn-delete-all" onClick={handleDeleteAllWeeks}>
                Denna &amp; alla veckor framåt
              </button>
              <button className="btn-cancel" onClick={() => setDeleteDialog(null)}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Planning;
